/** @jsxImportSource @emotion/react */
import React, { FC, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
    getDurationInMinutes,
    getEstimationCompletition,
    getTariff,
    getTime,
} from '../../../../utils/utils';
import { GuestTransactionStatus, ITransaction } from '../../../../api/transactions/types';
import { EStationPricingOption, ICharge } from '../../../../api/types';

import chargePanelStyles from './info-panel.styles';

type PropTypes = {
    guest: ITransaction;
    charge: ICharge;
};

type InfoRowTuple = readonly [ReactNode, ReactNode?, ReactNode?];

const InfoRow: FC<{
    row: InfoRowTuple;
    variant: 'default' | 'finish';
}> = ({ row, variant }) => {
    const [title, value, unit] = row;

    if (variant === 'finish') {
        return (
            <div css={chargePanelStyles.finishBlockRow}>
                <div css={chargePanelStyles.finishBlockTitle}>{title}</div>
                <div css={chargePanelStyles.infoFinishValue}>
                    {value}
                    <div css={chargePanelStyles.infoFinishValueSymbol}>{unit}</div>
                </div>
            </div>
        );
    }

    return (
        <div css={chargePanelStyles.infoBlock}>
            <div css={chargePanelStyles.infoBlockTitle}>{title}</div>
            <div css={chargePanelStyles.infoBlockValue}>
                {value}
                <div css={chargePanelStyles.infoBlockValueSymbol}>{unit}</div>
            </div>
        </div>
    );
};

const FinishBlock: FC<{ rows: InfoRowTuple[] }> = ({ rows }) => (
    <div css={chargePanelStyles.finishBlock}>
        {rows.map((r, idx) => (
            <InfoRow key={idx} row={r} variant="finish" />
        ))}
    </div>
);

export const InfoPanel: FC<PropTypes> = ({ guest, charge }) => {
    const intl = useIntl();
    const showCurrentTariffTitle =
        (guest as any)?.pricing?.type === 'tiered'
            ? Array.isArray((guest as any)?.pricing?.tieredPricesPeriods) &&
              (guest as any)?.pricing?.tieredPricesPeriods?.length > 0
            : Array.isArray((guest as any)?.pricing?.pricesPeriods) &&
              (guest as any)?.pricing?.pricesPeriods?.length > 0;

    const getConnectorInfo = () => {
        const { station } = guest;
        const connector = station.connectors.at(charge.connectorId - 1);
        return connector
            ? { name: connector.type, power: connector.power.toString() }
            : { name: '', power: '0' };
    };

    const getCurrent = () => {
        return charge?.meterValues?.currentImport?.at(-1) ?? 0;
    };

    const getDuration = () => {
        return getDurationInMinutes(charge.startAt, charge.stopAt);
    };

    const chargeData = useMemo(() => {
        const row = (title: ReactNode, value?: ReactNode, unit?: ReactNode): InfoRowTuple =>
            [title, value, unit] as const;

        const connectorRow = () =>
            row(
                intl.formatMessage({ id: 'connector' }),
                (charge?.connectorId < 10 ? 0 : '') + charge?.connectorId?.toString(),
                getConnectorInfo().name
            );

        const rateRow = () =>
            row(
                intl.formatMessage({ id: showCurrentTariffTitle ? 'currentTariff' : 'rate' }),
                ...getTariff(guest, intl.formatMessage)
            );

        const startAtRow = () => row(intl.formatMessage({ id: 'startAt' }), getTime(charge?.startAt));

        const currentRow = (): InfoRowTuple[] =>
            charge.stopAt ? [] : [row(intl.formatMessage({ id: 'current' }), getCurrent(), 'A')];

        const durationRow = () => {
            const limitView = guest.duration ? `/${guest.duration}` : '';
            return row(
                intl.formatMessage({ id: 'duration' }),
                `${getDuration()}${limitView}`,
                intl.formatMessage({ id: 'min' })
            );
        };

        const energyDeliveredRow = () => {
            const limitView = guest.energy ? `/${Number(guest.energy / 1000).toFixed(2)}` : '';
            return row(
                intl.formatMessage({ id: limitView ? 'develiveredTotal' : 'delivered' }),
                `${Number(charge.energy / 1000).toFixed(2)}${limitView}`,
                intl.formatMessage({ id: 'unit.kWh' })
            );
        }

        const amountRow = (id: string, minorUnits?: number): InfoRowTuple[] =>
            minorUnits
                ? [row(intl.formatMessage({ id }), Number((minorUnits ?? 0) / 100).toFixed(2), guest.pricing.currency)]
                : [];

        const hasExtraFees = !!(guest.idleFeeCalc?.price || guest.pricing?.activationFee);

        const costRows = (): InfoRowTuple[] => [
            ...(hasExtraFees
                ? [
                    ...amountRow('tariffDetails.fees.connectionFee', guest.pricing?.activationFee),
                    ...amountRow('chargingCost', guest.chargingPrice),
                    ...amountRow('connector.info.idleFee', guest.idleFeeCalc?.price),
                  ]
                : []),
            row(
                intl.formatMessage({ id: 'usedHeld' }),
                `${Number(guest.priceTotal / 100).toFixed(2)}/${Number(
                    (guest.initialPaymentIntentAmount ?? guest.paymentIntent?.amount ?? 0) / 100
                ).toFixed(2)}`,
                guest.pricing.currency
            ),
        ];

        const costRow = () =>
            row(
                intl.formatMessage({ id: 'chargingCost' }),
                Number((guest.chargingPrice ?? guest.priceTotal) / 100).toFixed(2),
                guest.pricing.currency
            );

        const estimationRow = (): InfoRowTuple[] => {
            const completion = getEstimationCompletition(guest);
            return completion
                ? [row(intl.formatMessage({ id: 'estimatedCompletition' }), completion)]
                : [];
        };

        const idleFeeRateRow = (): InfoRowTuple[] =>
            guest.pricing.idleFee?.enabled
                ? ([
                      row(
                          intl.formatMessage({ id: 'connector.info.idleFeeRate' }),
                          Number((guest.pricing.idleFee?.price ?? 0) / 100).toFixed(2),
                          `${guest.pricing.currency}/${intl.formatMessage({ id: 'min' })}`
                      ),
                  ] as const)
                : [];

        const totalRow = () =>
            row(
                intl.formatMessage({ id: 'total' }),
                Number(guest.priceTotal / 100).toFixed(2),
                guest.pricing.currency
            );

        if (charge.status === 1 && guest.status === GuestTransactionStatus.Completed) {
            return {
                main: [
                    connectorRow(),
                    durationRow(),
                    energyDeliveredRow(),
                    ...(showCurrentTariffTitle
                        ? []
                        : ([
                              row(
                                  intl.formatMessage({ id: 'rate' }),
                                  ...getTariff(guest, intl.formatMessage)
                              ),
                          ] as const)),
                    ...idleFeeRateRow(),
                ],
                breakdown: [
                    row(
                        `${intl.formatMessage({ id: 'tariffDetails.fees.connectionFee' })}`,
                        Number((guest.pricing?.activationFee ?? 0) / 100).toFixed(2),
                        guest.pricing.currency
                    ),
                    costRow(),
                    row(
                        `${guest?.idleFeeCalc?.minutes ?? 0} ${intl.formatMessage({
                            id: 'min',
                        })} ${intl.formatMessage({ id: 'connector.info.idleFee' })}`,
                        Number((guest.idleFeeCalc?.price ?? 0) / 100).toFixed(2),
                        guest.pricing.currency
                    ),
                ],
                totals: [totalRow()],
            };
        } else if (
            guest.pricing.pricingOption === EStationPricingOption.kwh &&
            guest.pricing.priceForKwh
        ) {
            return [
                connectorRow(),
                energyDeliveredRow(),
                rateRow(),
                ...idleFeeRateRow(),
                ...costRows(),
                startAtRow(),
                ...estimationRow(),
                durationRow(),
                ...currentRow(),
            ];
        } else if (guest.pricing.pricingOption === EStationPricingOption.free) {
            return [
                connectorRow(),
                energyDeliveredRow(),
                row(
                    intl.formatMessage({ id: showCurrentTariffTitle ? 'currentTariff' : 'rate' }),
                    intl.formatMessage({ id: 'payment.free' })
                ),
                startAtRow(),
                ...estimationRow(),
                durationRow(),
                ...currentRow(),
            ];
        } else {
            return [
                connectorRow(),
                energyDeliveredRow(),
                rateRow(),
                ...idleFeeRateRow(),
                ...costRows(),
                startAtRow(),
                ...estimationRow(),
                durationRow(),
                ...currentRow(),
            ];
        }
    }, [getConnectorInfo, getCurrent, getDuration, guest, intl, charge]);

    const sections = Array.isArray(chargeData) ? null : chargeData;

    return (
        <div
            style={{
                width: '100%',
            }}>
            <div css={chargePanelStyles.row}>
                {!sections ? (
                    (chargeData as InfoRowTuple[]).map((r, idx) => (
                        <InfoRow key={idx} row={r} variant="default" />
                    ))
                ) : (
                    <>
                        <FinishBlock rows={sections.main} />
                        <p css={chargePanelStyles.costBreakdownTitle}>{intl.formatMessage({ id: 'tariffDetails.costBreakdown' })}</p>
                        <FinishBlock rows={sections.breakdown} />
                        <FinishBlock rows={sections.totals} />
                    </>
                )}
            </div>
        </div>
    );
};
